import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function Users() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] = useState(false);
  const [deletedUserName, setDeletedUserName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    fullName: '',
    role: 'manager',
    isActive: true,
    pagePermissions: [],
    password: '',
    confirmPassword: ''
  });

  // All available pages
  const availablePages = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'students', name: 'Students' },
    { id: 'parents', name: 'Parents' },
    { id: 'teachers', name: 'Teachers' },
    { id: 'receptionists', name: 'Receptionists' },
    { id: 'transport-persons', name: 'Drivers' },
    { id: 'users', name: 'User' },
    { id: 'student-approvals', name: 'Review - Approve / Reject' },
    { id: 'classes', name: 'Classes' },
    { id: 'curriculum-form', name: 'Curriculum' },
    { id: 'class-schedule', name: 'Class Schedule' },
    { id: 'schedule-view', name: 'Schedule View' },
    { id: 'homework', name: 'Homework' },
    { id: 'exam-schedule', name: 'Exam Schedule' },
    { id: 'exam-report-cards', name: 'Exam Report Cards' },
    { id: 'report-cards', name: 'Report Cards' },
    { id: 'attendance-records', name: 'View Attendance' },
    { id: 'set-fees', name: 'Set Fees' },
    { id: 'fee-schedule', name: 'Fee Schedule' },
    { id: 'fee-records', name: 'Fee Records' },
    { id: 'student-fees', name: 'Student Fees' },
    { id: 'notices', name: 'Notices' },
    { id: 'chat', name: 'Chat' },
    { id: 'chat-groups', name: 'Chat Groups' },
    { id: 'admin-chats', name: 'Admin Chats' },
    { id: 'vehicle', name: 'Vehicle' },
    { id: 'routes', name: 'Routes' },
    { id: 'pickup-locations', name: 'Pickup Locations' },
    { id: 'bus-seat-management', name: 'Bus Seat Management' },
    { id: 'meals', name: 'Meals' },
    { id: 'bi-reports', name: 'BI & Reports' },
    { id: 'settings', name: 'Settings' },
    { id: 'timing', name: 'School Timings' },
    { id: 'schedule', name: 'Holiday Selection' },
    { id: 'annual-calendar-form', name: 'Academic Year' },
    { id: 'policies', name: 'Policies / Handbook' },
    { id: 'contact-us', name: 'Contact Us' },
    { id: 'upgrade', name: 'Upgrade' },
    { id: 'my-account', name: 'My Account' }
  ];

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchUsers();
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      let query = supabase
        .from('user_roles')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: false });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      username: user.username || '',
      email: user.email || '',
      fullName: user.full_name || '',
      role: user.role || 'manager',
      isActive: user.is_active !== false,
      pagePermissions: user.page_permissions || [],
      password: '',
      confirmPassword: ''
    });
  };

  const togglePagePermission = (pageId) => {
    setEditForm(prev => ({
      ...prev,
      pagePermissions: prev.pagePermissions.includes(pageId)
        ? prev.pagePermissions.filter(id => id !== pageId)
        : [...prev.pagePermissions, pageId]
    }));
  };

  const handleUpdate = async () => {
    try {
      // Validate password if provided
      if (editForm.password && editForm.password.trim() !== '') {
        if (editForm.password !== editForm.confirmPassword) {
          setError('Password and confirm password do not match');
          return;
        }
        if (editForm.password.length < 6) {
          setError('Password must be at least 6 characters long');
          return;
        }
      }

      // Update user_roles table
      const { error } = await supabase
        .from('user_roles')
        .update({
          username: editForm.username,
          email: editForm.email,
          full_name: editForm.fullName || null,
          role: editForm.role,
          page_permissions: editForm.pagePermissions,
          is_active: editForm.isActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingUser.id);

      if (error) throw error;

      // Update password if provided
      if (editForm.password && editForm.password.trim() !== '') {
        try {
          // Check if user has auth_user_id
          const { data: userData, error: userError } = await supabase
            .from('user_roles')
            .select('auth_user_id')
            .eq('id', editingUser.id)
            .single();

          if (!userError && userData && userData.auth_user_id) {
            // Update password using Supabase Auth Admin API
            const { error: passwordError } = await supabase.auth.admin.updateUserById(
              userData.auth_user_id,
              { password: editForm.password.trim() }
            );

            if (passwordError) {
              console.warn('Failed to update password:', passwordError);
              setError('User updated but password update failed. Please try updating password separately.');
            }
          } else {
            // If no auth_user_id, try to find user by email in auth.users
            const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
            if (!authError && authUsers) {
              const authUser = authUsers.users.find(u => u.email === editForm.email);
              if (authUser) {
                const { error: passwordError } = await supabase.auth.admin.updateUserById(
                  authUser.id,
                  { password: editForm.password.trim() }
                );
                if (passwordError) {
                  console.warn('Failed to update password:', passwordError);
                }
              }
            }
          }
        } catch (passwordErr) {
          console.warn('Error updating password:', passwordErr);
          setError('User updated but password update failed. Please try updating password separately.');
        }
      }

      setEditingUser(null);
      fetchUsers();
      setError('');
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user');
    }
  };

  const handleDelete = (userId) => {
    const user = users.find(u => u.id === userId);
    setUserToDelete({
      id: userId,
      name: user?.username || user?.email || 'User'
    });
    setShowDeleteConfirmPopup(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', userToDelete.id);

      if (error) throw error;

      // Store deleted user name for success popup
      const deletedName = userToDelete.name;

      // Refresh the list
      await fetchUsers();
      
      // Close confirmation popup and show success popup
      setShowDeleteConfirmPopup(false);
      setUserToDelete(null);
      setDeletedUserName(deletedName);
      setShowDeleteSuccessPopup(true);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
      setIsSubmitting(false);
      setShowDeleteConfirmPopup(false);
      setUserToDelete(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirmPopup(false);
    setUserToDelete(null);
  };

  const handleLogout = () => navigate('/schoollogin');

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content" style={{ padding: '2rem', paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
        <div className="content-header" style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ marginBottom: '0.25rem' }}>User Management</h1>
            <p style={{ margin: 0 }}>Manage users, roles, and page permissions</p>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/school/add-user')}
            style={{ padding: '0.75rem 1.25rem', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            + Add User
          </button>
        </div>

        {error && (
          <div className="message error" style={{ marginBottom: '1.5rem' }}>
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {loadingUsers ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="loading-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            padding: '2rem', 
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>Users ({users.length})</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F3F4F6', borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Username</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Full Name</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Role</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Pages</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '12px' }}>{user.username}</td>
                      <td style={{ padding: '12px' }}>{user.email}</td>
                      <td style={{ padding: '12px' }}>{user.full_name || 'N/A'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: user.role === 'head_teacher' ? '#8B5CF6' : '#10B981',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {user.role === 'head_teacher' ? 'Head Teacher' : 'Manager'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '300px' }}>
                          {(user.page_permissions || []).slice(0, 3).map(pageId => {
                            const page = availablePages.find(p => p.id === pageId);
                            return page ? (
                              <span key={pageId} style={{
                                padding: '2px 6px',
                                backgroundColor: '#EFF6FF',
                                borderRadius: '3px',
                                fontSize: '11px'
                              }}>
                                {page.name}
                              </span>
                            ) : null;
                          })}
                          {(!user.page_permissions || user.page_permissions.length === 0) && (
                            <span style={{ color: '#999', fontSize: '12px' }}>No permissions</span>
                          )}
                          {user.page_permissions && user.page_permissions.length > 3 && (
                            <span style={{ color: '#6B7280', fontSize: '11px' }}>
                              +{user.page_permissions.length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: user.is_active ? '#10B981' : '#9CA3AF',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setViewingUser(user)}
                            style={{ 
                              padding: '6px 12px',
                              backgroundColor: '#3B82F6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                            title="View Details"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                            <span>View</span>
                          </button>
                          <button
                            onClick={() => handleEdit(user)}
                            style={{ 
                              padding: '6px 12px',
                              backgroundColor: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                            title="Edit"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            style={{ 
                              padding: '6px 12px',
                              backgroundColor: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                            title="Delete"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                            <span>Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF' }}>
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }} onClick={() => setEditingUser(null)}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: 0,
              maxWidth: '800px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #FF8B3D 0%, #FFA366 100%)',
                padding: '1.5rem 2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px'
              }}>
                <h2 style={{ margin: 0, color: 'white', fontSize: '1.5rem', fontWeight: '700' }}>Edit User</h2>
                <button
                  onClick={() => setEditingUser(null)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                >
                  ×
                </button>
              </div>

              {/* Content */}
              <div style={{
                padding: '2rem',
                overflowY: 'auto',
                flex: 1,
                background: '#F9FAFB'
              }}>
                {error && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#FEE2E2',
                    border: '2px solid #EF4444',
                    borderRadius: '8px',
                    color: '#991B1B',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '500'
                  }}>
                    <span>⚠️</span>
                    <span>{error}</span>
                    <button
                      onClick={() => setError('')}
                      style={{
                        marginLeft: 'auto',
                        background: 'none',
                        border: 'none',
                        color: '#991B1B',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
                {/* Basic Information Section */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ 
                    color: '#1F2937', 
                    fontSize: '1.1rem', 
                    fontWeight: '600', 
                    marginBottom: '1rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #E5E7EB'
                  }}>Basic Information</h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div className="form-group">
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                        Username <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={editForm.username}
                        onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                        required
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          fontSize: '1rem', 
                          border: '2px solid #E5E7EB', 
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#FF8B3D'}
                        onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                        Email <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          fontSize: '1rem', 
                          border: '2px solid #E5E7EB', 
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#FF8B3D'}
                        onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={editForm.fullName}
                      onChange={(e) => setEditForm(prev => ({ ...prev, fullName: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '0.75rem', 
                        fontSize: '1rem', 
                        border: '2px solid #E5E7EB', 
                        borderRadius: '8px',
                        transition: 'all 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#FF8B3D'}
                      onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                        Role
                      </label>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          fontSize: '1rem', 
                          border: '2px solid #E5E7EB', 
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#FF8B3D'}
                        onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                      >
                        <option value="head_teacher">Head Teacher</option>
                        <option value="manager">Manager</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.5rem' }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem', 
                        cursor: 'pointer',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '2px solid #E5E7EB',
                        width: '100%',
                        transition: 'all 0.2s',
                        backgroundColor: 'white'
                      }}
                      onMouseEnter={(e) => e.target.style.borderColor = '#FF8B3D'}
                      onMouseLeave={(e) => e.target.style.borderColor = '#E5E7EB'}
                      >
                        <input
                          type="checkbox"
                          checked={editForm.isActive}
                          onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Active</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Password Change Section */}
                <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: 'white', borderRadius: '12px', border: '2px solid #E5E7EB' }}>
                  <h3 style={{ 
                    color: '#1F2937', 
                    fontSize: '1.1rem', 
                    fontWeight: '600', 
                    marginBottom: '1rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #E5E7EB'
                  }}>Change Password (Optional)</h3>
                  <p style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                    Leave blank if you don't want to change the password
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                        New Password
                      </label>
                      <input
                        type="password"
                        value={editForm.password}
                        onChange={(e) => setEditForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Enter new password"
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          fontSize: '1rem', 
                          border: '2px solid #E5E7EB', 
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#FF8B3D'}
                        onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        value={editForm.confirmPassword}
                        onChange={(e) => setEditForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Confirm new password"
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          fontSize: '1rem', 
                          border: '2px solid #E5E7EB', 
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#FF8B3D'}
                        onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                      />
                    </div>
                  </div>
                </div>

                {/* Page Permissions Section */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ 
                    color: '#1F2937', 
                    fontSize: '1.1rem', 
                    fontWeight: '600', 
                    marginBottom: '1rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #E5E7EB'
                  }}>
                    Page Permissions <span style={{ color: '#EF4444' }}>*</span>
                  </h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
                    gap: '12px',
                    padding: '1.5rem',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    maxHeight: '350px',
                    overflowY: 'auto'
                  }}>
                    {availablePages.map(page => (
                      <label 
                        key={page.id}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px',
                          cursor: 'pointer',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          border: '2px solid transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                          e.currentTarget.style.borderColor = '#E5E7EB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editForm.pagePermissions.includes(page.id)}
                          onChange={() => togglePagePermission(page.id)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>{page.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ 
                padding: '1.5rem 2rem',
                borderTop: '2px solid #E5E7EB',
                backgroundColor: 'white',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end',
                borderBottomLeftRadius: '16px',
                borderBottomRightRadius: '16px'
              }}>
                <button 
                  type="button" 
                  onClick={() => setEditingUser(null)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#E5E7EB';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#F3F4F6';
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={handleUpdate}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #FF8B3D 0%, #FFA366 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 6px rgba(255, 139, 61, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(255, 139, 61, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 6px rgba(255, 139, 61, 0.3)';
                  }}
                >
                  Update User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        {viewingUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }} onClick={() => setViewingUser(null)}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: 0,
              maxWidth: '700px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div style={{ 
                background: 'linear-gradient(135deg, #FF8B3D 0%, #FFA366 100%)',
                padding: '1.5rem 2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <div>
                  <h2 style={{ margin: 0, color: 'white', fontSize: '1.5rem', fontWeight: '700' }}>User Details</h2>
                  <p style={{ margin: '0.25rem 0 0 0', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                    View complete user information
                  </p>
                </div>
                <button
                  onClick={() => setViewingUser(null)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                >
                  ×
                </button>
              </div>

              {/* Content */}
              <div style={{ 
                padding: '2rem',
                overflowY: 'auto',
                flex: 1,
                background: '#F9FAFB'
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.25rem'
                }}>
                  {/* Username */}
                  <div style={{
                    backgroundColor: 'white',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: '#EEF2FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <label style={{ 
                        fontWeight: '600', 
                        color: '#6B7280',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Username</label>
                    </div>
                    <div style={{ 
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#111827',
                      wordBreak: 'break-word'
                    }}>
                      {viewingUser.username}
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{
                    backgroundColor: 'white',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: '#F0FDF4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                          <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                      </div>
                      <label style={{ 
                        fontWeight: '600', 
                        color: '#6B7280',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Email</label>
                    </div>
                    <div style={{ 
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: '#111827',
                      wordBreak: 'break-word'
                    }}>
                      {viewingUser.email}
                    </div>
                  </div>

                  {/* Full Name */}
                  <div style={{
                    backgroundColor: 'white',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <label style={{ 
                        fontWeight: '600', 
                        color: '#6B7280',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Full Name</label>
                    </div>
                    <div style={{ 
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: '#111827'
                    }}>
                      {viewingUser.full_name || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not provided</span>}
                    </div>
                  </div>

                  {/* Role */}
                  <div style={{
                    backgroundColor: 'white',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: viewingUser.role === 'head_teacher' ? '#F3E8FF' : '#D1FAE5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={viewingUser.role === 'head_teacher' ? '#8B5CF6' : '#10B981'} strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </div>
                      <label style={{ 
                        fontWeight: '600', 
                        color: '#6B7280',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Role</label>
                    </div>
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      backgroundColor: viewingUser.role === 'head_teacher' ? '#8B5CF6' : '#10B981',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      display: 'inline-block'
                    }}>
                      {viewingUser.role === 'head_teacher' ? 'Head Teacher' : 'Manager'}
                    </span>
                  </div>

                  {/* Status */}
                  <div style={{
                    backgroundColor: 'white',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: viewingUser.is_active ? '#D1FAE5' : '#FEE2E2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={viewingUser.is_active ? '#10B981' : '#EF4444'} strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </div>
                      <label style={{ 
                        fontWeight: '600', 
                        color: '#6B7280',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Status</label>
                    </div>
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      backgroundColor: viewingUser.is_active ? '#10B981' : '#9CA3AF',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      display: 'inline-block'
                    }}>
                      {viewingUser.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Created At */}
                  <div style={{
                    backgroundColor: 'white',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: '#E0E7FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </div>
                      <label style={{ 
                        fontWeight: '600', 
                        color: '#6B7280',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Created At</label>
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#111827'
                    }}>
                      {viewingUser.created_at ? new Date(viewingUser.created_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Page Permissions - Full Width */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '10px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  marginTop: '1.25rem'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: '#FEF3C7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="9" y1="3" x2="9" y2="21"></line>
                        <line x1="3" y1="9" x2="21" y2="9"></line>
                      </svg>
                    </div>
                    <label style={{ 
                      fontWeight: '600', 
                      color: '#6B7280',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Page Permissions</label>
                  </div>
                  {viewingUser.page_permissions && viewingUser.page_permissions.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {viewingUser.page_permissions.map(pageId => {
                        const page = availablePages.find(p => p.id === pageId);
                        return page ? (
                          <span key={pageId} style={{
                            padding: '8px 12px',
                            backgroundColor: '#EFF6FF',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            color: '#1E40AF',
                            fontWeight: '500',
                            border: '1px solid #DBEAFE'
                          }}>
                            {page.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '1rem',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '6px',
                      textAlign: 'center',
                      color: '#9CA3AF',
                      fontSize: '0.875rem',
                      fontStyle: 'italic'
                    }}>
                      No permissions assigned
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div style={{ 
                padding: '1.5rem 2rem',
                borderTop: '1px solid #E5E7EB',
                backgroundColor: 'white',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end',
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px'
              }}>
                <button
                  type="button"
                  onClick={() => setViewingUser(null)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#E5E7EB';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#F3F4F6';
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewingUser(null);
                    handleEdit(viewingUser);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #FF8B3D 0%, #FFA366 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 6px rgba(255, 139, 61, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(255, 139, 61, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 6px rgba(255, 139, 61, 0.3)';
                  }}
                >
                  Edit User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        {showDeleteConfirmPopup && userToDelete && (
          <div 
            className="popup-overlay" 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              animation: 'fadeIn 0.3s ease-in'
            }}
            onClick={cancelDeleteUser}
          >
            <div 
              style={{
                background: '#FFFFFF',
                borderRadius: '20px',
                padding: '3rem',
                maxWidth: '500px',
                width: '90%',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'slideUp 0.4s ease-out',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative circles */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                zIndex: 0
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                zIndex: 0
              }}></div>

              {/* Delete Icon */}
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
                position: 'relative',
                zIndex: 1,
                animation: 'scaleIn 0.5s ease-out'
              }}>
                <span style={{ 
                  fontSize: '4rem', 
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  lineHeight: 1
                }}>🗑️</span>
              </div>

              {/* Title */}
              <h2 style={{
                color: '#1F2937',
                fontSize: '2rem',
                fontWeight: '700',
                marginBottom: '0.5rem',
                position: 'relative',
                zIndex: 1
              }}>
                Delete User?
              </h2>

              {/* Message */}
              <p style={{
                color: '#6B7280',
                fontSize: '1rem',
                marginBottom: '1.5rem',
                lineHeight: '1.6',
                position: 'relative',
                zIndex: 1
              }}>
                Are you sure you want to <strong style={{ color: '#EF4444' }}>PERMANENTLY DELETE</strong> <strong>{userToDelete.name}</strong>?
                <br /><br />
                This will:
                <br />
                • Remove the user from the database completely
                <br />
                • Delete their login account and all permissions
                <br />
                <strong style={{ color: '#EF4444' }}>This action CANNOT be undone!</strong>
              </p>

              {/* Buttons */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 1
              }}>
                <button
                  onClick={cancelDeleteUser}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    background: '#FFFFFF',
                    color: '#6B7280',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: isSubmitting ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.background = '#F9FAFB';
                      e.target.style.borderColor = '#D1D5DB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#FFFFFF';
                    e.target.style.borderColor = '#E5E7EB';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteUser}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: '#FFFFFF',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: isSubmitting ? 0.5 : 1,
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                  }}
                >
                  {isSubmitting ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Success Popup */}
        {showDeleteSuccessPopup && (
          <div 
            className="popup-overlay" 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              animation: 'fadeIn 0.3s ease-in'
            }}
            onClick={() => setShowDeleteSuccessPopup(false)}
          >
            <div 
              style={{
                background: '#FFFFFF',
                borderRadius: '20px',
                padding: '3rem',
                maxWidth: '500px',
                width: '90%',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'slideUp 0.4s ease-out',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative circles */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                zIndex: 0
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                zIndex: 0
              }}></div>

              {/* Success Icon */}
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
                position: 'relative',
                zIndex: 1,
                animation: 'scaleIn 0.5s ease-out'
              }}>
                <span style={{ 
                  fontSize: '4rem', 
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  lineHeight: 1
                }}>✓</span>
              </div>

              {/* Title */}
              <h2 style={{
                color: '#1F2937',
                fontSize: '2rem',
                fontWeight: '700',
                marginBottom: '0.5rem',
                position: 'relative',
                zIndex: 1
              }}>
                User Deleted Successfully! 🎉
              </h2>

              {/* Message */}
              <p style={{
                color: '#6B7280',
                fontSize: '1rem',
                marginBottom: '2rem',
                lineHeight: '1.6',
                position: 'relative',
                zIndex: 1
              }}>
                <strong>{deletedUserName}</strong> has been permanently deleted from the database.
              </p>

              {/* Button */}
              <button
                onClick={() => setShowDeleteSuccessPopup(false)}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* CSS Animations */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(30px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes scaleIn {
            from { transform: scale(0); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}

export default Users;
